import React, {
  createContext,
  useMemo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { refreshAuthToken as requestRefreshAuthToken } from '../services/AuthService';
import {
  STORAGE_KEYS,
  persistAuthToken,
  readStorageItem,
  readStorageJSON,
  removeStorageItem,
  resolveStoredAuthToken,
  safeParseJSON,
  writeStorageItem,
  writeStorageJSON,
} from '../utils/storage';
import { clearAppQueryClientPersistence } from '../queries/queryClient';
import { clearHomeBootCache } from '../services/HomeBootService';
import { dataFlowLog, summarizeToken, summarizeUser } from '../utils/dataFlowLogger';
import { setAuthBridge } from '../services/authBridge';
import { actorIdOf, communityIdOf, syncQueryOwner, retireQueryOwner, getActiveQueryOwner, ownsQueryScope } from '../queries/queryOwnership';

const SESSION_STATES = {
  SIGNED_OUT: 'signed-out',
  ACTIVE: 'active',
  REFRESHING: 'refreshing',
  EXPIRED: 'expired',
};

const defaultAuthContext = {
  hasProvider: false,
  token: null,
  setToken: () => {},
  refreshToken: null,
  setRefreshToken: () => {},
  user: null,
  setUser: () => {},
  sessionState: null,
  shouldPromptReauth: false,
  setShouldPromptReauth: () => {},
  refreshSession: async () => false,
  triggerSessionExpiry: () => {},
  signOut: () => {},
};

const AuthContext = createContext(defaultAuthContext);

export const useAuthContext = () => useContext(AuthContext);

const normalizeStoredRefreshToken = (rawValue) => {
  if (!rawValue) {
    return null;
  }

  const parsed = safeParseJSON(rawValue, rawValue);
  if (typeof parsed === 'string') {
    return parsed;
  }
  if (parsed && typeof parsed === 'object' && typeof parsed.refreshToken === 'string') {
    return parsed.refreshToken;
  }
  return null;
};

const readInitialRefreshToken = () => {
  const persisted = normalizeStoredRefreshToken(
    readStorageItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN, { fallback: null }),
  );
  if (persisted) {
    return persisted;
  }

  return normalizeStoredRefreshToken(
    readStorageItem(STORAGE_KEYS.LEGACY_REFRESH_TOKEN, { fallback: null }),
  );
};

const readInitialUser = () => {
  const sessionUser = readStorageJSON(STORAGE_KEYS.AUTH_USER, {
    area: 'session',
    fallback: null,
  });
  if (sessionUser) {
    return sessionUser;
  }

  const authUser = readStorageJSON(STORAGE_KEYS.AUTH_USER, { fallback: null });
  if (authUser) {
    return authUser;
  }

  return readStorageJSON(STORAGE_KEYS.LEGACY_USER, { fallback: null });
};

export const AuthProvider = ({ children }) => {
  const [token, updateToken] = useState(() => resolveStoredAuthToken());
  const [refreshToken, updateRefreshToken] = useState(() => readInitialRefreshToken());
  const [user, updateUser] = useState(() => readInitialUser());
  const [sessionState, setSessionState] = useState(() => ({
    status: resolveStoredAuthToken() ? SESSION_STATES.ACTIVE : SESSION_STATES.SIGNED_OUT,
    reason: null,
    source: null,
  }));
  const [shouldPromptReauth, setShouldPromptReauth] = useState(false);

  const authRef = useRef(null);
  if (!authRef.current) {
    authRef.current = { token, refreshToken, user, generation: 0, mounted: true };
    authRef.current.owner = syncQueryOwner({ token, refreshToken, user });
  }
  // Commit ownership synchronously before React publishes a new render. Pending
  // requests and retained setters cannot write through the old generation.
  const changeAuth = useCallback((field, value, update) => {
    const current = authRef.current;
    if (!current.mounted) return;
    const next = typeof value === 'function' ? value(current[field]) : value;
    const changesOwner = field === 'user'
      ? actorIdOf(next) !== actorIdOf(current.user)
        || communityIdOf(next?.community ?? next?.communityId) !== communityIdOf(current.user?.community ?? current.user?.communityId)
      : next !== current[field];
    current[field] = next;
    if (field === 'token') persistAuthToken(next);
    if (changesOwner) current.generation += 1;
    current.owner = syncQueryOwner(current);
    update(next);
  }, []);
  const setToken = useCallback(value => changeAuth('token', value, updateToken), [changeAuth]);
  const setRefreshToken = useCallback(value => changeAuth('refreshToken', value, updateRefreshToken), [changeAuth]);
  const setUser = useCallback(value => changeAuth('user', value, updateUser), [changeAuth]);
  const isGenerationCurrent = useCallback(generation => authRef.current.mounted
    && authRef.current.generation === generation, []);
  const sessionStateRef = useRef(sessionState);
  const refreshInFlightRef = useRef(null);
  const bootstrapRefreshAttemptedRef = useRef(null);
  const expireTimerRef = useRef(null);
  const preemptTimerRef = useRef(null);

  const clearExpiryTimers = useCallback(() => {
    if (expireTimerRef.current) {
      clearTimeout(expireTimerRef.current);
      expireTimerRef.current = null;
    }
    if (preemptTimerRef.current) {
      clearTimeout(preemptTimerRef.current);
      preemptTimerRef.current = null;
    }
  }, []);

  const finalizeSessionExpiry = useCallback((reason = 'Session expired', generation = authRef.current.generation) => {
    if (!isGenerationCurrent(generation)) return;
    dataFlowLog('auth-context.session.expire', { reason });
    clearExpiryTimers();
    refreshInFlightRef.current = null;
    setSessionState({ status: SESSION_STATES.EXPIRED, reason, source: null });
    setShouldPromptReauth(true);
    clearAppQueryClientPersistence();
    clearHomeBootCache();
    try {
      if (typeof window !== 'undefined') {
        const { pathname, search, hash } = window.location;
        const resumeUrl = `${pathname || ''}${search || ''}${hash || ''}`;
        writeStorageItem(STORAGE_KEYS.LAST_PROTECTED_ROUTE, resumeUrl, { area: 'session' });
      }
    } catch {
      // ignore
    }
    setToken(null);
    setRefreshToken(null);
  }, [clearExpiryTimers, isGenerationCurrent, setRefreshToken, setToken]);

  // The single refresh path for the whole app. Reads the latest refresh token
  // from the ref, dedupes concurrent callers via refreshInFlightRef, and resolves
  // to `{ token }` on success (so the API client can retry with it) or `null` on
  // failure. Never throws.
  const attemptRefresh = useCallback(async ({ source = 'manual', reason = null } = {}) => {
    const generation = authRef.current.generation;
    const currentRefreshToken = authRef.current.refreshToken;
    if (!isGenerationCurrent(generation) || !currentRefreshToken) return null;
    if (refreshInFlightRef.current?.generation === generation) {
      return refreshInFlightRef.current.promise;
    }
    dataFlowLog('auth-context.refresh.start', {
      source,
      reason,
      hasRefreshToken: Boolean(currentRefreshToken),
    });

    const operation = { generation, promise: null };
    refreshInFlightRef.current = operation;
    const refreshPromise = (async () => {
      setSessionState((prev) => ({
        status: SESSION_STATES.REFRESHING,
        reason: reason || prev.reason,
        source,
      }));

      try {
        const { ok, data } = await requestRefreshAuthToken(currentRefreshToken);
        if (!isGenerationCurrent(generation)) return null;
        if (!ok || !data?.token) {
          throw new Error(data?.message || 'Unable to refresh session');
        }

        const nextRefreshToken = data.refreshToken || data.newRefreshToken || null;
        // Setters update the live auth ref synchronously, so a subsequent
        // refresh sees the rotated refresh credential before React renders.
        setToken(data.token);
        if (nextRefreshToken) {
          setRefreshToken(nextRefreshToken);
        }
        if (data.user) setUser(data.user);
        setSessionState({ status: SESSION_STATES.ACTIVE, reason: null, source: null });
        setShouldPromptReauth(false);
        dataFlowLog('auth-context.refresh.success', {
          source,
          token: summarizeToken(data?.token),
          user: summarizeUser(data?.user),
          hasRefreshToken: Boolean(nextRefreshToken),
        });
        return { token: data.token };
      } catch (error) {
        dataFlowLog('auth-context.refresh.error', {
          source,
          message: error?.message || 'Unknown error',
        });
        return null;
      } finally {
        if (refreshInFlightRef.current === operation) {
          refreshInFlightRef.current = null;
        }
      }
    })();

    operation.promise = refreshPromise;
    return refreshPromise;
  }, [isGenerationCurrent, setRefreshToken, setToken, setUser]);

  const handleTokenExpired = useCallback(async (reason = 'Session expired', generation = authRef.current.generation) => {
    if (!isGenerationCurrent(generation)) return;
    clearExpiryTimers();
    if (!authRef.current.refreshToken) {
      finalizeSessionExpiry(reason, generation);
      return;
    }
    const refreshed = await attemptRefresh({ source: 'expiry', reason });
    if (!refreshed) {
      finalizeSessionExpiry(reason, generation);
    }
  }, [attemptRefresh, clearExpiryTimers, finalizeSessionExpiry, isGenerationCurrent]);

  const scheduleTokenTimers = useCallback((expSeconds) => {
    clearExpiryTimers();
    if (!expSeconds) return;

    const generation = authRef.current.generation;
    const now = Date.now();
    const expiryMs = expSeconds * 1000 - now;
    if (expiryMs <= 0) {
      handleTokenExpired('Session expired', generation);
      return;
    }

    const preemptMs = Math.max(expiryMs - 60000, 0);
    if (preemptMs > 0) {
      preemptTimerRef.current = setTimeout(() => {
        if (!isGenerationCurrent(generation)) return;
        attemptRefresh({ source: 'preemptive', reason: 'Session expiring soon' }).catch((error) => {
          dataFlowLog('auth-context.refresh.unhandled-error', {
            source: 'preemptive',
            message: error?.message || 'Unknown error',
          });
        });
      }, preemptMs);
    }

    expireTimerRef.current = setTimeout(() => {
      handleTokenExpired('Session expired', generation);
    }, expiryMs);
  }, [attemptRefresh, clearExpiryTimers, handleTokenExpired, isGenerationCurrent]);

  useEffect(() => {
    try {
      persistAuthToken(token);
    } catch (e) {
      dataFlowLog('auth-context.storage.sync-token.error', {
        message: e?.message || 'Unknown error',
      });
    }
  }, [token]);

  useEffect(() => {
    try {
      if (refreshToken) {
        writeStorageItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN, refreshToken);
      } else {
        removeStorageItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
      }

      removeStorageItem(STORAGE_KEYS.LEGACY_REFRESH_TOKEN);
    } catch (e) {
      dataFlowLog('auth-context.storage.sync-refresh-token.error', {
        message: e?.message || 'Unknown error',
      });
    }
  }, [refreshToken]);

  // Register this AuthContext's refresh/expiry with the API-client bridge so a
  // 401 on any request runs the one deduped refresh-and-retry above.
  useEffect(() => {
    return setAuthBridge({
      refreshSession: () => attemptRefresh({ source: 'api-client', reason: 'Unauthorized response' }),
      expireSession: (reason) => finalizeSessionExpiry(reason || 'Session expired'),
    });
  }, [attemptRefresh, finalizeSessionExpiry]);

  useEffect(() => {
    try {
      if (user === undefined || user === null) {
        removeStorageItem(STORAGE_KEYS.AUTH_USER, { area: 'session' });
      } else {
        writeStorageJSON(STORAGE_KEYS.AUTH_USER, user, { area: 'session' });
      }

      removeStorageItem(STORAGE_KEYS.AUTH_USER);
      removeStorageItem(STORAGE_KEYS.LEGACY_USER);
    } catch (e) {
      dataFlowLog('auth-context.storage.sync-user.error', {
        message: e?.message || 'Unknown error',
      });
    }
  }, [user]);

  useEffect(() => {
    sessionStateRef.current = sessionState;
  }, [sessionState]);

  useEffect(() => {
    if (token) {
      bootstrapRefreshAttemptedRef.current = null;
      return;
    }

    if (!refreshToken) {
      bootstrapRefreshAttemptedRef.current = null;
      return;
    }

    const generation = authRef.current.generation;
    if (bootstrapRefreshAttemptedRef.current === generation) return;
    bootstrapRefreshAttemptedRef.current = generation;
    attemptRefresh({ source: 'bootstrap', reason: 'Restore session from refresh token' })
      .then((refreshed) => {
        if (!refreshed) {
          finalizeSessionExpiry('Session expired', generation);
        }
      })
      .catch(() => {
        finalizeSessionExpiry('Session expired', generation);
      });
  }, [attemptRefresh, finalizeSessionExpiry, refreshToken, token]);

  useEffect(() => {
    if (!token) {
      clearExpiryTimers();
      if (!refreshToken && sessionStateRef.current.status !== SESSION_STATES.EXPIRED) {
        setSessionState((prev) => (
          prev.status === SESSION_STATES.SIGNED_OUT
            ? prev
            : { status: SESSION_STATES.SIGNED_OUT, reason: null, source: null }
        ));
        setUser(null);
      }
      return;
    }

    try {
      const decoded = jwtDecode(token);
      if (decoded.exp && decoded.exp < Date.now() / 1000) {
        handleTokenExpired('Session expired');
        return;
      }

      if (!user) {
        setUser(decoded);
      }
      dataFlowLog('auth-context.token.active', {
        token: summarizeToken(token),
        user: summarizeUser(user || decoded),
      });

      setSessionState((prev) => (
        prev.status === SESSION_STATES.ACTIVE && prev.reason === null && prev.source === null
          ? prev
          : { status: SESSION_STATES.ACTIVE, reason: null, source: null }
      ));
      setShouldPromptReauth(false);
      scheduleTokenTimers(decoded.exp);
    } catch (e) {
      dataFlowLog('auth-context.token.invalid', {
        message: e?.message || 'Invalid auth token',
      });
      finalizeSessionExpiry('Invalid session token');
    }
  }, [
    token,
    user,
    refreshToken,
    clearExpiryTimers,
    finalizeSessionExpiry,
    handleTokenExpired,
    scheduleTokenTimers,
    setUser,
  ]);

  useLayoutEffect(() => {
    authRef.current.mounted = true;
    authRef.current.owner = syncQueryOwner(authRef.current);
    return () => {
      const active = getActiveQueryOwner();
      authRef.current.mounted = false;
      authRef.current.generation += 1;
      clearExpiryTimers();
      refreshInFlightRef.current = null;
      if (active && active === authRef.current.owner && ownsQueryScope(active)) retireQueryOwner();
    };
  }, [clearExpiryTimers]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event) => {
      if (!event?.key) return;
      if (event.key === STORAGE_KEYS.AUTH_REFRESH_TOKEN || event.key === STORAGE_KEYS.AUTH_USER) {
        if (event.key === STORAGE_KEYS.AUTH_REFRESH_TOKEN) {
          const nextRefreshToken = event.newValue ? normalizeStoredRefreshToken(event.newValue) : null;
          setRefreshToken(nextRefreshToken);
          if (!nextRefreshToken) {
            setToken(null);
          }
        }
        if (event.key === STORAGE_KEYS.AUTH_USER) {
          const nextUser = event.newValue ? safeParseJSON(event.newValue, null) : null;
          setUser(nextUser);
        }
      }
    };

    // Cross-tab sync only. 401 handling now lives entirely in the API client's
    // refresh-and-retry path (see services/apiClient.js + authBridge), so the
    // app no longer monkey-patches window.fetch to catch every 401 — that global
    // interceptor raced with the client and could expire a valid session.
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [setRefreshToken, setToken, setUser]);

  const signOut = useCallback(() => {
    if (!authRef.current.mounted) return;
    dataFlowLog('auth-context.sign-out.start');
    clearExpiryTimers();
    refreshInFlightRef.current = null;
    bootstrapRefreshAttemptedRef.current = null;
    setSessionState({ status: SESSION_STATES.SIGNED_OUT, reason: null, source: null });
    setShouldPromptReauth(false);
    setToken(undefined);
    setRefreshToken(undefined);
    setUser(undefined);
    clearHomeBootCache();

    try {
      removeStorageItem(STORAGE_KEYS.AUTH_TOKEN, { area: 'session' });
      removeStorageItem(STORAGE_KEYS.AUTH_TOKEN);
      removeStorageItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
      removeStorageItem(STORAGE_KEYS.AUTH_USER, { area: 'session' });
      removeStorageItem(STORAGE_KEYS.AUTH_USER);
      removeStorageItem(STORAGE_KEYS.LEGACY_AUTH_TOKEN);
      removeStorageItem(STORAGE_KEYS.LEGACY_USER);
      removeStorageItem(STORAGE_KEYS.LEGACY_REFRESH_TOKEN);
      clearAppQueryClientPersistence();
    } catch (e) {
      dataFlowLog('auth-context.storage.clear.error', {
        message: e?.message || 'Unknown error',
      });
    }
    dataFlowLog('auth-context.sign-out.done');
  }, [clearExpiryTimers, setRefreshToken, setToken, setUser]);

  useEffect(() => {
    dataFlowLog('auth-context.state.updated', {
      token: summarizeToken(token),
      user: summarizeUser(user),
      sessionStatus: sessionState?.status || null,
      sessionReason: sessionState?.reason || null,
      sessionSource: sessionState?.source || null,
      shouldPromptReauth,
    });
  }, [sessionState, shouldPromptReauth, token, user]);

  const generation = authRef.current.generation;
  const isSessionCurrent = useCallback(() => isGenerationCurrent(generation), [generation, isGenerationCurrent]);
  const value = useMemo(
    () => ({
      hasProvider: true,
      isSessionCurrent,
      token,
      setToken,
      refreshToken,
      setRefreshToken,
      user,
      setUser,
      sessionState,
      shouldPromptReauth,
      setShouldPromptReauth,
      refreshSession: attemptRefresh,
      triggerSessionExpiry: reason => finalizeSessionExpiry(reason, generation),
      signOut,
    }),
    [
      generation,
      isSessionCurrent,
      attemptRefresh,
      finalizeSessionExpiry,
      refreshToken,
      setRefreshToken,
      setShouldPromptReauth,
      setToken,
      setUser,
      shouldPromptReauth,
      sessionState,
      signOut,
      token,
      user,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
